import { TipTapContent } from '@/components/canvas/docxContent';
import apiClient from '@/services/api_client';
import { DocumentItem, DocumentVersion, Visibility } from '@/types';

export type DocumentListOrderBy =
    | 'updated_at_desc'
    | 'updated_at_asc'
    | 'name_asc'
    | 'name_desc';
export type DocumentListVisibility = 'private' | 'public';

export type DocumentPreviewItem = {
    id: string;
    page_number: number;
    content: string | null;
    error?: boolean;
};

export type DocumentListItem = {
    id: string;
    name: string;
    type: string;
    visibility: string;
    created_at: string;
    updated_at: string;
    total_pages: number;
    preview_items: DocumentPreviewItem[];
    can_modify_metadata: boolean;
    can_edit: boolean;
};

export type DocumentListResponse = {
    total: number;
    items: DocumentListItem[];
};

export type GetDocumentsListParams = {
    documentType?: string;
    visibility: DocumentListVisibility;
    orderBy?: DocumentListOrderBy;
    offset?: number;
    limit?: number;
    previewLimit?: number;
};

export type UploadPptxResponse = {
    document_id: string;
    name: string;
    total_pages: number;
};

export async function createDocument(
    tenantId: string,
    documentType: string,
    name?: string,
    content?: string,
    referrerId?: string,
    chatSessionId?: string,
    docContent?: TipTapContent
): Promise<DocumentItem> {
    const url = '/document';
    const body = {
        tenant_id: tenantId,
        document_type: documentType,
        name: name ?? null,
        content: content ?? null,
        doc_content: docContent ?? null,
        referrer_id: referrerId ?? null,
        chat_session_id: chatSessionId ?? null
    };
    const response = await apiClient.post(url, body);
    return response;
}

export async function getDocuments(tenantId: string): Promise<DocumentItem[]> {
    const url = `/documents?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function getDocumentsList(
    tenantId: string,
    params: GetDocumentsListParams,
    signal?: AbortSignal
): Promise<DocumentListResponse> {
    const searchParams = new URLSearchParams({
        tenant_id: tenantId,
        document_type: params.documentType ?? 'slides',
        visibility: params.visibility,
        order_by: params.orderBy ?? 'updated_at_desc',
        offset: String(params.offset ?? 0),
        limit: String(params.limit ?? 9),
        preview_limit: String(params.previewLimit ?? 3)
    });
    const response = await apiClient.get(
        `/documents/library?${searchParams.toString()}`,
        {
            signal
        }
    );
    return await response.json();
}

export async function getDocument(
    documentId: string,
    tenantId: string
): Promise<DocumentItem> {
    const url = `/document/${documentId}?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function getDocumentCurrentVersion(
    documentId: string,
    tenantId: string
): Promise<DocumentVersion> {
    const url = `/document/${documentId}/version?tenant_id=${tenantId}`;
    const response = await apiClient.get(url);
    return await response.json();
}

export async function updateDocument(
    documentId: string,
    tenantId: string,
    name?: string,
    content?: string,
    referrerId?: string,
    visibility?: DocumentListVisibility
): Promise<DocumentItem> {
    const url = `/document/${documentId}`;
    const body: Record<string, string | null> = {
        tenant_id: tenantId,
        name: name ?? null,
        content: content ?? null,
        referrer_id: referrerId ?? null
    };
    if (visibility !== undefined) {
        body.visibility = visibility;
    }
    return await apiClient.patch(url, body);
}

export async function updateDocumentVersion(
    documentId: string,
    tenantId: string,
    versionId: string,
    content: string
): Promise<DocumentVersion> {
    const url = `/document/${documentId}/version/${versionId}`;
    const body = {
        tenant_id: tenantId,
        content: content
    };
    return await apiClient.patch(url, body);
}

export async function deleteDocument(
    documentId: string,
    tenantId: string
): Promise<void> {
    const url = `/document/${documentId}?tenant_id=${tenantId}`;
    return await apiClient.delete(url, {});
}

export async function uploadPptx(
    tenantId: string,
    file: File,
    visibility: DocumentListVisibility = Visibility.PRIVATE
): Promise<UploadPptxResponse> {
    const chunkSize = 1 * 1024 * 1024; // 1MB
    const totalChunks = Math.max(Math.ceil(file.size / chunkSize), 1);
    let offset = 0;
    let filePath: string | undefined;
    let lastResponse: UploadPptxResponse | undefined;

    for (let chunkNumber = 0; chunkNumber < totalChunks; chunkNumber++) {
        const chunk = file.slice(offset, offset + chunkSize);
        const formData = new FormData();
        formData.append('file', chunk);
        formData.append('name', file.name);
        formData.append('chunk_number', String(chunkNumber));
        formData.append('total_chunks', String(totalChunks));
        formData.append('tenant_id', tenantId);
        formData.append('visibility', visibility);
        if (filePath) {
            formData.append('file_path', filePath);
        }

        const response = await apiClient.postFormData(
            '/upload-pptx',
            formData
        );
        filePath = response.file_path;
        lastResponse = response;
        offset += chunkSize;
    }

    return lastResponse as UploadPptxResponse;
}
